import { useIdle, useStorage, useTimestamp, watchThrottled } from '@vueuse/core';
import { onlineManager } from '@tanstack/vue-query';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useAuth } from '../stores/authStore';
import { usePos } from '../stores/posStore';

interface UseIdleLogoutOptions {
  /** Inactivity in milliseconds after which the session is closed. */
  timeout: number;
  /** Only end PIN sessions, so an unattended device locks but a desk login does not. */
  posOnly?: boolean;
  /** If true, the app will be set to offline mode when the user is logged out due to inactivity. */
  setOfflineOnLogout?: boolean;
}

/**
 * Outlives the page so a reload cannot hand a user a fresh timeout: a device
 * that was already idle for longer than the timeout locks again on boot instead
 * of resuming the session.
 */
const LAST_ACTIVE_STORAGE_KEY = 'acquisition-store:last-active';

/** How often the last activity is written out. Bounds how far the persisted
 *  stamp can lag behind reality - always in the direction of locking early. */
const PERSIST_THROTTLE = 10 * 1000;

export const useIdleLogout = ({ timeout, posOnly, setOfflineOnLogout }: UseIdleLogoutOptions) => {
  const { isLoggedIn, isPinSession, logout } = useAuth();
  const { isPosMode } = usePos();
  // `listenForVisibilityChange` is off so a tab coming back into view does not
  // count as activity: a device that was put down for longer than the timeout
  // must lock whether the page stayed alive in the background or was discarded
  // and reloaded.
  const { lastActive } = useIdle(timeout, { listenForVisibilityChange: false });

  const persistedLastActive = useStorage(LAST_ACTIVE_STORAGE_KEY, Date.now());
  // `lastActive` is seeded with the page load time, so it only becomes the
  // truthful stamp once a real event has moved it - until then the persisted
  // one carries the inactivity from before the reload.
  const interactedSinceLoad = ref(false);
  const now = useTimestamp({ interval: 1000 });

  const lastActiveAt = computed(() =>
    interactedSinceLoad.value ? lastActive.value : persistedLastActive.value,
  );
  const idle = computed(() => now.value - lastActiveAt.value >= timeout);

  watch(lastActive, () => (interactedSinceLoad.value = true));
  watchThrottled(lastActive, (activeAt) => (persistedLastActive.value = activeAt), {
    throttle: PERSIST_THROTTLE,
    trailing: true,
  });

  const logoutIfIdle = () => {
    if (!idle.value || !isLoggedIn.value) return;
    if (posOnly && !isPinSession.value) return;

    logout(false);
  };

  watch(idle, logoutIfIdle);

  // A session restored from storage can already be past its timeout, and a
  // value that starts out `true` is never announced by the watcher above. Runs
  // at `onBeforeMount` - the same point the unconditional POS sign-out used to
  // sit at - rather than during setup, so the stores are settled first.
  onBeforeMount(logoutIfIdle);

  watch([isLoggedIn, isPosMode], ([loggedIn, isPosMode]) => {
    if (!setOfflineOnLogout) return;
    if (!loggedIn && (isPosMode || !posOnly)) onlineManager.setOnline(false);
    else onlineManager.setOnline(true);
  });

  // Run this in setup so it is registered before the stores fetch
  if (setOfflineOnLogout && !isLoggedIn.value && (isPosMode.value || !posOnly)) {
    onlineManager.setOnline(false);
  }

  return { idle, lastActive: lastActiveAt };
};
